/**
 * MyComplaintsPage — /my-complaints
 *
 * The complaints the signed-in user logged (GET /complaints?mine=1, newest
 * first). Status filter chips with counts, a search box, one-tap status
 * buttons per complaint, Edit (the same dialog Admin > Complaints uses),
 * remarks and action taken on the row, and photo thumbnails that open the
 * full-screen viewer. Replaces the Complaints tab that used to live in
 * My Registrations (/my-registrations?tab=complaints now redirects here).
 *
 * Access: every signed-in user. The server scopes the list to the caller
 * and only lets the owner (or admin / manager) change a complaint.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { AlertCircle, Loader2, Pencil, Phone, Plus, RefreshCw, Search } from 'lucide-react';
import { api } from '../lib/store';
import { logger } from '../lib/logger';
import ComplaintDialog from '../components/ComplaintDialog';
import {
  COMPLAINT_STATUSES, STATUS_CHIP, STATUS_LABEL, fmtDate, EditComplaintDialog,
} from '../components/admin/ComplaintsPanel';
import { photoUrls, PhotoThumb, useImageViewer } from '../components/ImageViewer';

const statusOf = (c) => (STATUS_LABEL[c?.status] ? c.status : 'pending');

const SEARCH_FIELDS = [
  'customer_name', 'mobile', 'description', 'action_taken', 'remarks',
  'attended_by_name', 'complaint_number',
];

function Detail({ label, children, testId }) {
  if (!children) return null;
  return (
    <p className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-heda-sub" data-testid={testId}>
      <span className="font-semibold text-heda-ink">{label}:</span> {children}
    </p>
  );
}

function ComplaintCard({ c, onSetStatus, onEdit, onOpenPhotos }) {
  const [saving, setSaving] = useState('');
  const status = statusOf(c);
  const photos = photoUrls(c.images, c.image_urls);

  const setStatus = async (value) => {
    if (value === status || saving) return;
    setSaving(value);
    try { await onSetStatus(c, value); } finally { setSaving(''); }
  };

  return (
    <Card className="rounded-2xl border-heda-line bg-white p-3 shadow-none sm:p-4" data-testid={`my-complaint-${c.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-mono-data text-[10.5px] text-heda-mute">
            #{c.complaint_number || (c.id || '').slice(0, 8)} · {fmtDate(c.created_at)}
          </p>
          <p className="truncate text-[15px] font-bold text-heda-ink">{c.customer_name}</p>
        </div>
        <span
          className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CHIP[status]}`}
          data-testid={`my-complaint-status-${c.id}`}
        >
          {STATUS_LABEL[status]}
        </span>
      </div>

      <div className="mt-2 space-y-1">
        {c.mobile && (
          <a href={`tel:${c.mobile}`} className="inline-flex items-center gap-1 text-[12.5px] tabular-nums text-heda-sub hover:text-heda-gold">
            <Phone className="h-3 w-3" /> {c.mobile}
          </a>
        )}
        <Detail label="Complaint">{c.description}</Detail>
        <Detail label="Action taken" testId={`my-complaint-action-${c.id}`}>{c.action_taken}</Detail>
        <Detail label="Remarks" testId={`my-complaint-remarks-${c.id}`}>{c.remarks}</Detail>
        <Detail label="Attended by">{c.attended_by_name}</Detail>
      </div>

      {photos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`my-complaint-photos-${c.id}`}>
          {photos.map((src, i) => (
            <PhotoThumb
              key={src}
              src={src}
              className="h-14 w-14"
              onClick={() => onOpenPhotos(photos, i)}
              label={`Open photo ${i + 1} of ${photos.length}`}
              testId={`my-complaint-photo-${c.id}-${i}`}
            />
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-dashed border-heda-line pt-3">
        <div className="heda-seg" role="group" aria-label="Status" data-testid={`my-complaint-status-buttons-${c.id}`}>
          {COMPLAINT_STATUSES.map((st) => (
            <button
              key={st.value}
              type="button"
              className={`min-h-9 whitespace-nowrap ${status === st.value ? 'is-on' : 'hover:text-heda-ink'}`}
              aria-pressed={status === st.value}
              disabled={!!saving}
              onClick={() => setStatus(st.value)}
              data-testid={`my-complaint-set-${st.value}-${c.id}`}
            >
              {saving === st.value && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}
              {st.label}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit(c)}
          className="ml-auto h-9 rounded-xl border-heda-line bg-white text-heda-ink hover:border-heda-gold hover:bg-white hover:text-heda-gold"
          data-testid={`my-complaint-edit-${c.id}`}
        >
          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
        </Button>
      </div>
    </Card>
  );
}

export default function MyComplaintsPage() {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [salespeople, setSalespeople] = useState([]);
  const [openViewer, viewer] = useImageViewer('Complaint photo');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getComplaints({ mine: 1, limit: 500 });
      setComplaints(res.data?.complaints || []);
    } catch (err) {
      logger.error('Failed to load my complaints:', err);
      toast.error(err.response?.data?.detail || 'Failed to load your complaints');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // "Attended by" choices for the Edit dialog.
  useEffect(() => {
    api.getSalespeople()
      .then((res) => setSalespeople(res.data?.salespeople || []))
      .catch(() => setSalespeople([]));
  }, []);

  // The update is partial on the server: only the status is sent.
  const setComplaintStatus = async (c, status) => {
    try {
      const res = await api.updateComplaint(c.id, { status });
      setComplaints((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...(res.data || {}), status } : x)));
      toast.success(`Complaint marked ${STATUS_LABEL[status] || status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update the complaint');
    }
  };

  const counts = useMemo(() => {
    const n = { all: complaints.length, pending: 0, in_progress: 0, closed: 0 };
    complaints.forEach((c) => { n[statusOf(c)] += 1; });
    return n;
  }, [complaints]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return complaints.filter((c) => (
      (statusFilter === 'all' || statusOf(c) === statusFilter)
      && (!q || SEARCH_FIELDS.some((f) => String(c[f] || '').toLowerCase().includes(q)))
    ));
  }, [complaints, search, statusFilter]);

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="my-complaints-page">
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
          <CardTitle className="flex items-center gap-2 font-display text-[17px] font-bold text-heda-ink">
            <AlertCircle className="h-4 w-4 text-heda-gold" />
            My Complaints
            <span className="rounded-full bg-heda-cream px-2 py-0.5 text-[10px] font-bold tabular-nums text-heda-mute">{complaints.length}</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              className="h-9 rounded-xl border-heda-line bg-white text-heda-ink hover:border-heda-gold hover:bg-white hover:text-heda-gold"
              aria-label="Refresh"
              data-testid="my-complaints-refresh"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setLogOpen(true)}
              className="h-9 rounded-xl bg-heda-ink text-white hover:bg-black"
              data-testid="my-complaints-new"
            >
              <Plus className="h-3.5 w-3.5" /> Log complaint
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-heda-mute" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer, mobile, complaint, remarks…"
              className="h-10 rounded-xl bg-heda-cream/50 pl-9 text-sm"
              data-testid="my-complaints-search"
            />
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="my-complaints-status-filter">
            {[{ value: 'all', label: 'All' }, ...COMPLAINT_STATUSES].map((st) => {
              const on = statusFilter === st.value;
              return (
                <button
                  key={st.value}
                  type="button"
                  onClick={() => setStatusFilter(st.value)}
                  aria-pressed={on}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                    on ? (STATUS_CHIP[st.value] || 'border-heda-ink bg-heda-ink text-white') : 'border-heda-line bg-white text-heda-sub hover:border-heda-mute'
                  }`}
                  data-testid={`my-complaints-filter-${st.value}`}
                >
                  {st.label} <span className={`text-[10px] tabular-nums ${on && st.value === 'all' ? 'text-white/70' : 'text-heda-mute'}`}>{counts[st.value]}</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {loading && complaints.length === 0 && (
        <p className="py-10 text-center text-sm text-heda-mute"><Loader2 className="mr-2 inline h-4 w-4 animate-spin text-heda-gold" />Loading…</p>
      )}
      {!loading && complaints.length === 0 && (
        <p className="py-10 text-center text-sm text-heda-sub" data-testid="my-complaints-empty">
          You have not logged any complaints yet.
        </p>
      )}
      {complaints.length > 0 && visible.length === 0 && (
        <p className="py-10 text-center text-sm text-heda-sub">No complaints match this filter.</p>
      )}

      <div className="space-y-2.5" data-testid="my-complaints-list">
        {visible.map((c) => (
          <ComplaintCard
            key={c.id}
            c={c}
            onSetStatus={setComplaintStatus}
            onEdit={setEditing}
            onOpenPhotos={openViewer}
          />
        ))}
      </div>

      <EditComplaintDialog
        complaint={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchData(); }}
        salespeople={salespeople}
      />
      <ComplaintDialog open={logOpen} onOpenChange={setLogOpen} onCreated={() => fetchData()} />
      {viewer}
    </div>
  );
}
